import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	SiteFeatureConfigSymbol,
	HeadContentSymbol,
	DomReadySymbol,
	METASITE_APP_DEF_ID,
} from '@wix/thunderbolt-symbols'
import type { IAppWillMountHandler, IHeadContent } from '@wix/thunderbolt-symbols'
import type { CustomCssSiteConfig } from './types'
import { name } from './symbols'
import { customCssId, pageId, ViewEnvironment } from './constants'
import { getPageCss } from '@wix/ambassador-velo-css-v1-page-css/http'
import { SessionManagerSymbol } from 'feature-session-manager'
import type { ISessionManager } from 'feature-session-manager'
import { createHttpClient } from '@wix/http-client'

export const getInstance = (getAppInstanceByAppDefId: ISessionManager['getAppInstanceByAppDefId']) => () =>
	getAppInstanceByAppDefId(METASITE_APP_DEF_ID) || ''

export const fetchStyles = async (
	gridAppId: string,
	viewEnvironment: ViewEnvironment,
	getAppInstance: () => string,
	requestFactory: typeof getPageCss
) => {
	if (gridAppId) {
		const httpClient = createHttpClient({ getAppToken: getAppInstance, isSSR: !process.env.browser })
		try {
			const cssCode = await httpClient
				.request(
					requestFactory({
						gridAppId,
						pageId,
						...(viewEnvironment === ViewEnvironment.PUBLIC ? {} : { viewEnvironment }),
					})
				)
				.then((response) => response.data.pageCss?.content)
				.catch((error) => {
					if (error?.response?.status === 404) {
						// User possibly deleted CSS file
						return ''
					} else {
						throw new Error(error)
					}
				})
			return `/* Users Custom CSS code */\n ${cssCode}`
		} catch (e) {
			console.error('failed to fetch Custom CSS', e)
		}
	}
}

const wrapInStyleElement = (css: string) => {
	return `<style id=${customCssId}>${css}</style>`
}

export const ServerCustomCss = withDependencies(
	[named(SiteFeatureConfigSymbol, name), HeadContentSymbol, SessionManagerSymbol],
	(
		siteFeatureConfig: CustomCssSiteConfig,
		head: IHeadContent,
		sessionManager: ISessionManager
	): IAppWillMountHandler => {
		return {
			async appWillMount() {
				const cssCode = await fetchStyles(
					siteFeatureConfig.gridAppId,
					ViewEnvironment.PUBLIC,
					getInstance(sessionManager.getAppInstanceByAppDefId),
					getPageCss
				)
				if (cssCode) {
					head.addCustomCss(wrapInStyleElement(cssCode))
				}
			},
		}
	}
)

export const ClientCustomCss = withDependencies(
	[named(SiteFeatureConfigSymbol, name), DomReadySymbol, SessionManagerSymbol],
	(
		siteFeatureConfig: CustomCssSiteConfig,
		domReadyPromise: Promise<void>,
		sessionManager: ISessionManager
	): IAppWillMountHandler => {
		return {
			async appWillMount() {
				await domReadyPromise
				const customCSSElement = document.getElementById(customCssId)
				if (customCSSElement) {
					return
				}
				const cssCode = await fetchStyles(
					siteFeatureConfig.gridAppId,
					ViewEnvironment.PUBLIC,
					getInstance(sessionManager.getAppInstanceByAppDefId),
					getPageCss
				)
				if (cssCode) {
					const styleElement = window.document.createElement('style')
					styleElement.setAttribute('id', customCssId)
					styleElement.innerHTML = cssCode
					window.document.head.appendChild(styleElement)
				}
			},
		}
	}
)
